<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

const { t } = useI18n()

const SUPPORT_EMAIL = 'incentivospg@pdm.es'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const copied = ref(false)

function close() {
  emit('update:visible', false)
}

async function copyEmail() {
  try {
    await window.navigator.clipboard.writeText(SUPPORT_EMAIL)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    // clipboard API not available
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :dismissable-mask="true"
    :closable="true"
    class="contact-dialog"
    @update:visible="emit('update:visible', $event)"
  >
    <template #header>
      <div class="contact-header">
        <div class="contact-icon-badge">
          <i class="pi pi-envelope" aria-hidden="true"></i>
        </div>
        <span class="contact-kicker">{{ t('contact.kicker') }}</span>
      </div>
    </template>

    <div class="contact-body">
      <h2 class="contact-heading">
        {{ t('contact.heading') }}
      </h2>
      <p class="contact-text">{{ t('contact.text') }}</p>

      <div class="email-cta">
        <a
          :href="`mailto:${SUPPORT_EMAIL}`"
          class="email-link"
          :aria-label="t('contact.sendEmailAria', { email: SUPPORT_EMAIL })"
        >
          <i class="pi pi-envelope" aria-hidden="true"></i>
          <span>{{ SUPPORT_EMAIL }}</span>
        </a>
        <button
          type="button"
          class="copy-btn"
          :aria-label="copied ? t('contact.emailCopied') : t('contact.copyEmail')"
          @click="copyEmail"
        >
          <i :class="copied ? 'pi pi-check' : 'pi pi-copy'" aria-hidden="true"></i>
        </button>
      </div>

      <p class="response-text">
        {{ t('contact.responseTime') }}
      </p>
    </div>

    <template #footer>
      <Button :label="t('common.close')" outlined class="w-full" @click="close" />
    </template>
  </Dialog>
</template>

<style scoped>
.contact-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.contact-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #003DA5 0%, #0052D4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 61, 165, 0.3);
}

.contact-kicker {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--neutral-900);
}

.contact-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.25rem 0 0.5rem;
}

.contact-heading {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--neutral-900);
  line-height: 1.4;
  margin: 0;
}

.contact-text {
  color: var(--neutral-600);
  margin: 0;
  font-size: 0.9375rem;
}

.email-cta {
  display: flex;
  align-items: stretch;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #16a34a;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
}

.email-link {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.3;
  min-width: 0;
  word-break: break-all;
  transition: background 0.15s ease;
}

.email-link .pi {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.email-link:hover {
  background: rgba(255, 255, 255, 0.1);
}

.copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  background: rgba(0, 0, 0, 0.15);
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 1rem;
  transition: background 0.15s ease;
}

.copy-btn:hover {
  background: rgba(0, 0, 0, 0.25);
}

.copy-btn:active {
  background: rgba(0, 0, 0, 0.35);
}

.response-text {
  color: var(--neutral-500);
  font-size: 0.8125rem;
  margin: 0;
  line-height: 1.5;
}
</style>

<style>
.contact-dialog {
  width: 90vw;
  max-width: 480px;
}

.contact-dialog .p-dialog-footer {
  padding-top: 0.75rem;
}
</style>
